import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import GavelIcon from '@mui/icons-material/Gavel';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { customFetcher } from '../hooks/useFetch';
import { useCourts, useFederalStates } from './hooks';

interface IAssignment {
  idAssignment?: number;
  idCourt?: number;
  Court?: string;
  idCourtRole?: number;
  CourtRole?: string;
}

interface IEnrichedAdminUnit {
  idAdministrativeUnit: number;
  AdministrativeUnit: string;
  idFederalState?: number;
  FederalState?: string;
  Assignments?: IAssignment[];
}

interface IProps {
  value: number | null;
  onChange: (
    idAdministrativeUnit: number | null,
    unit?: IEnrichedAdminUnit | null
  ) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  label?: string;
}

/**
 * Komfort-Auswahl für Gemeinde/Gemarkung mit:
 *  - Bundesland-Filter
 *  - Gericht-Filter (Autocomplete) — schränkt Treffer auf Gemarkungen ein, in
 *    deren Court_CourtRole_AdministrativeUnit-Zuordnung dieses Gericht (mit
 *    irgendeiner Rolle) aktiv ist.
 *  - Suche/Autocomplete über den Gemarkungsnamen
 *  - Chips pro aktiver Gerichtsrolle (Amtsgericht, Grundbuchamt, Finanzamt,
 *    Handelsregister, …) — direkt in den Suggestions sichtbar.
 */
export const GemarkungSelector: React.FC<IProps> = ({
  value,
  onChange,
  disabled,
  size = 'small',
  label = 'Gemeinde/Gemarkung',
}) => {
  const { data: enriched, isLoading } = useQuery<IEnrichedAdminUnit[]>({
    refetchOnWindowFocus: false,
    queryKey: ['/administrativeunit/enriched'],
    queryFn: () => customFetcher({ endpoint: '/administrativeunit/enriched' }),
  });
  const { federalStates } = useFederalStates();
  const { courts } = useCourts();

  const [federalStateFilter, setFederalStateFilter] = useState<number | ''>('');
  const [courtFilter, setCourtFilter] = useState<{
    idCourt: number;
    Court: string;
  } | null>(null);

  const filteredOptions = useMemo(() => {
    if (!enriched) return [] as IEnrichedAdminUnit[];
    return enriched.filter(unit => {
      if (
        federalStateFilter !== '' &&
        unit.idFederalState !== federalStateFilter
      ) {
        return false;
      }
      if (courtFilter) {
        const has = (unit.Assignments || []).some(
          a => a.idCourt === courtFilter.idCourt
        );
        if (!has) return false;
      }
      return true;
    });
  }, [enriched, federalStateFilter, courtFilter]);

  const selectedUnit = useMemo(() => {
    if (!enriched || !value) return null;
    return enriched.find(u => u.idAdministrativeUnit === value) || null;
  }, [enriched, value]);

  return (
    <Stack spacing={1.5}>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={5}>
          <FormControl fullWidth size={size}>
            <InputLabel>Bundesland (Filter)</InputLabel>
            <Select
              label='Bundesland (Filter)'
              value={federalStateFilter}
              onChange={e =>
                setFederalStateFilter(
                  e.target.value === '' ? '' : Number(e.target.value)
                )
              }
              disabled={disabled}
            >
              <MenuItem value=''>– alle Bundesländer –</MenuItem>
              {(federalStates || []).map(fs => (
                <MenuItem key={fs.idFederalState} value={fs.idFederalState}>
                  {fs.FederalState}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={7}>
          <Autocomplete
            size={size}
            options={courts || []}
            getOptionLabel={o => o.Court || ''}
            isOptionEqualToValue={(o, v) => o.idCourt === v.idCourt}
            value={courtFilter}
            onChange={(_, v) =>
              setCourtFilter(v ? { idCourt: v.idCourt, Court: v.Court } : null)
            }
            disabled={disabled}
            renderInput={params => (
              <TextField {...params} label='Gericht (Filter)' />
            )}
          />
        </Grid>
      </Grid>

      <Autocomplete
        size={size}
        loading={isLoading}
        options={filteredOptions}
        value={selectedUnit}
        onChange={(_, v) => onChange(v ? v.idAdministrativeUnit : null, v)}
        disabled={disabled}
        getOptionLabel={o => o.AdministrativeUnit}
        isOptionEqualToValue={(o, v) =>
          o.idAdministrativeUnit === v.idAdministrativeUnit
        }
        filterOptions={(options, { inputValue }) => {
          const q = inputValue.trim().toLowerCase();
          if (!q) return options.slice(0, 100);
          return options
            .filter(o => o.AdministrativeUnit.toLowerCase().includes(q))
            .slice(0, 100);
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            placeholder='Tippen, um zu suchen…'
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <LocationCityIcon
                    fontSize='small'
                    sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }}
                  />
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const distinctRoles: { idCourtRole?: number; CourtRole?: string }[] =
            [];
          (option.Assignments || []).forEach(a => {
            if (
              a.CourtRole &&
              !distinctRoles.find(r => r.idCourtRole === a.idCourtRole)
            ) {
              distinctRoles.push({
                idCourtRole: a.idCourtRole,
                CourtRole: a.CourtRole,
              });
            }
          });
          return (
            <Box
              component='li'
              {...props}
              key={option.idAdministrativeUnit}
              sx={{ alignItems: 'flex-start !important', flexWrap: 'wrap' }}
            >
              <Box sx={{ width: '100%' }}>
                <Stack
                  direction='row'
                  spacing={1}
                  alignItems='baseline'
                  flexWrap='wrap'
                >
                  <Typography variant='body1' fontWeight={600}>
                    {option.AdministrativeUnit}
                  </Typography>
                  {option.FederalState && (
                    <Typography variant='caption' color='text.secondary'>
                      {option.FederalState}
                    </Typography>
                  )}
                </Stack>
                {distinctRoles.length > 0 ? (
                  <Stack
                    direction='row'
                    spacing={0.5}
                    sx={{ mt: 0.5 }}
                    flexWrap='wrap'
                    useFlexGap
                  >
                    {distinctRoles.map(r => (
                      <Chip
                        key={r.idCourtRole}
                        size='small'
                        variant='outlined'
                        icon={<GavelIcon sx={{ fontSize: 14 }} />}
                        label={r.CourtRole}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant='caption' color='warning.main'>
                    Keine Gerichtsrollen zugeordnet
                  </Typography>
                )}
              </Box>
            </Box>
          );
        }}
      />

      {selectedUnit && (
        <Box
          sx={{
            bgcolor: 'action.hover',
            borderRadius: 1,
            p: 1.25,
          }}
        >
          <Typography variant='caption' color='text.secondary'>
            Aktive Gerichtsrollen für{' '}
            <strong>{selectedUnit.AdministrativeUnit}</strong>
          </Typography>
          <Stack
            direction='row'
            spacing={0.5}
            sx={{ mt: 0.5 }}
            flexWrap='wrap'
            useFlexGap
          >
            {(selectedUnit.Assignments || []).length > 0 ? (
              (selectedUnit.Assignments || []).map(a => (
                <Chip
                  key={a.idAssignment}
                  size='small'
                  icon={<GavelIcon sx={{ fontSize: 14 }} />}
                  label={`${a.CourtRole}: ${a.Court}`}
                  variant='outlined'
                />
              ))
            ) : (
              <Typography variant='caption' color='warning.main'>
                Keine Gerichtsrollen zugeordnet — bitte in der Ämterverwaltung
                pflegen.
              </Typography>
            )}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
